import { format } from "date-fns";
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Lock, Unlock, CheckCircle, RefreshCw, Download, Check } from "lucide-react";
import LoadingIndicator from "@/components/LoadingIndicator";
import { useMonthEndStock } from "@/hooks/useMonthEndStock";
import { useToast } from "@/hooks/use-toast";
import { useLocation, useParams } from "wouter";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

export const MonthEndStockDetail = () => {
  const { formatCost, format: formatDecimal } = useDecimalPrecision();
  const { toast } = useToast();
  const [_, navigate] = useLocation();
  const params = useParams();
  const periodId = params.id;
  const [showApplyValuesDialog, setShowApplyValuesDialog] = useState(false);

  const { usePeriodById, applyValues, isApplyingValues, lockPeriod, isLocking, unlockPeriod, isUnlocking } = useMonthEndStock();
  const { data: selectedPeriodData, isLoading: isLoadingDetail } = usePeriodById(periodId);

  const handleApplyValues = async () => {
    if (!periodId) return;

    try {
      const result = await applyValues(periodId);
      toast({
        title: "Values Applied Successfully",
        description: `Updated ${result.data.data.itemsUpdated} items with recalculated values. This action cannot be undone.`,
        duration: 5000,
        icon: <CheckCircle size={30} className="animate-spin-once text-green-400" />,
      });
      setShowApplyValuesDialog(false);
    } catch (error: any) {
      toast({
        title: "Apply Values Failed",
        description: error.response?.data?.message || error.message || "Failed to apply values",
        variant: "destructive",
      });
    }
  };

  const handleLockPeriod = async () => {
    if (!periodId) return;

    try {
      await lockPeriod({ periodId });
      toast({
        title: "Period Locked",
        description: "The period has been locked to prevent backdated edits",
        duration: 5000,
        icon: <CheckCircle size={30} className="animate-spin-once text-green-400" />,
      });
    } catch (error: any) {
      toast({
        title: "Lock Failed",
        description: error.response?.data?.message || error.message || "Failed to lock period",
        variant: "destructive",
      });
    }
  };

  const handleUnlockPeriod = async () => {
    if (!periodId) return;

    try {
      await unlockPeriod({ periodId });
      toast({
        title: "Period Unlocked",
        description: "The period has been unlocked and can now accept backdated changes",
        duration: 5000,
        icon: <CheckCircle size={30} className="animate-spin-once text-green-400" />,
      });
    } catch (error: any) {
      toast({
        title: "Unlock Failed",
        description: error.response?.data?.message || error.message || "Failed to unlock period",
        variant: "destructive",
      });
    }
  };

  if (isLoadingDetail) return <LoadingIndicator />;

  if (!selectedPeriodData) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-950/20 dark:border-red-900/50 dark:text-red-400 px-4 py-3 rounded">
          <p className="font-semibold mb-2">Period Not Found</p>
          <Button onClick={() => navigate("/month-end-stock")} className="mt-2">
            Back to List
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">
            Period {selectedPeriodData.periodNumber} - {selectedPeriodData.voucherNumber}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {format(new Date(selectedPeriodData.startDate), "dd/MM/yyyy")} -{" "}
            {format(new Date(selectedPeriodData.endDate), "dd/MM/yyyy")}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/month-end-stock")}>
          Back to List
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Total Items</p>
            <p className="text-3xl font-bold mt-2">{selectedPeriodData.totalItems}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Total Value Change</p>
            <p className={`text-3xl font-bold mt-2 ${(selectedPeriodData.totalValueChange || 0) >= 0 ? "text-green-600" : "text-red-600"}`}>
              {(selectedPeriodData.totalValueChange || 0) >= 0 ? "+" : ""}RM {formatDecimal(Math.abs(selectedPeriodData.totalValueChange || 0))}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Status</p>
            <div className="mt-2">
              <Badge variant={selectedPeriodData.locked ? "default" : "secondary"} className="text-base px-3 py-1">
                {selectedPeriodData.locked ? (
                  <>
                    <Lock className="h-4 w-4 mr-1" />
                    Locked
                  </>
                ) : (
                  "Unlocked"
                )}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Variances Summary */}
      {selectedPeriodData.hasVariances && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="font-semibold text-yellow-900">⚠️ Variances Detected</p>
          <p className="text-sm text-yellow-800 mt-1">
            This period contains quantity or weighted average variances. Review the details below.
          </p>
        </div>
      )}

      {/* Items Table */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <Label className="text-lg font-semibold">Recalculation Results</Label>
              <p className="text-sm text-muted-foreground">
                Before and after comparison for each item
              </p>
            </div>

            <div className="border rounded-lg overflow-auto">
              <Table>
                <TableHeader className="bg-slate-50 dark:bg-slate-800">
                  <TableRow>
                    <TableHead>Item Code</TableHead>
                    <TableHead className="text-right">Original Qty</TableHead>
                    <TableHead className="text-right">Recalculated Qty</TableHead>
                    <TableHead className="text-right">Qty Variance</TableHead>
                    <TableHead className="text-right">Original WA</TableHead>
                    <TableHead className="text-right">Recalculated WA</TableHead>
                    <TableHead className="text-right">WA Variance</TableHead>
                    <TableHead className="text-right">Value Change</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedPeriodData.items?.map((item) => (
                    <TableRow key={item.id} className={item.hasQtyVariance || item.hasWaVariance ? "bg-yellow-50" : ""}>
                      <TableCell className="font-medium">{item.itemCode}</TableCell>
                      <TableCell className="text-right">{formatDecimal(item.originalQty)}</TableCell>
                      <TableCell className="text-right">{formatDecimal(item.recalculatedQty)}</TableCell>
                      <TableCell className={`text-right ${item.hasQtyVariance ? "text-red-600 font-semibold" : ""}`}>
                        {item.qtyVariance > 0 ? "+" : ""}{formatDecimal(item.qtyVariance)}
                      </TableCell>
                      <TableCell className="text-right">{formatCost(item.originalWa)}</TableCell>
                      <TableCell className="text-right">{formatCost(item.recalculatedWa)}</TableCell>
                      <TableCell className={`text-right ${item.hasWaVariance ? "text-red-600 font-semibold" : ""}`}>
                        {item.waVariance > 0 ? "+" : ""}{formatCost(item.waVariance)}
                      </TableCell>
                      <TableCell className={`text-right ${item.valueVariance !== 0 ? "font-semibold" : ""}`}>
                        {item.valueVariance > 0 ? "+" : ""}RM {formatDecimal(item.valueVariance)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Apply Values Section */}
            <div className={`border rounded-lg p-4 ${selectedPeriodData.valuesApplied ? "bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-900/50" : "bg-orange-50 border-orange-200 dark:bg-orange-950/20 dark:border-orange-900/50"}`}>
              <p className={`font-semibold ${selectedPeriodData.valuesApplied ? "text-green-900" : "text-orange-900"}`}>
                {selectedPeriodData.valuesApplied ? "✓ Values Applied" : "Apply Recalculated Values"}
              </p>
              <p className={`text-sm mt-1 ${selectedPeriodData.valuesApplied ? "text-green-800" : "text-orange-800"}`}>
                {selectedPeriodData.valuesApplied ? (
                  <>
                    The recalculated values have been applied to all {selectedPeriodData.totalItems} items.
                    This action has been completed and cannot be repeated.
                  </>
                ) : (
                  <>
                    Click "Apply Values" to update all {selectedPeriodData.totalItems} items with their recalculated
                    quantities and weighted averages. <span className="font-semibold">This action cannot be undone.</span>
                  </>
                )}
              </p>
            </div>

            {/* Lock Period Section */}
            <div className={`border rounded-lg p-4 ${selectedPeriodData.locked ? "bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700" : "bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-900/50"}`}>
              <p className={`font-semibold ${selectedPeriodData.locked ? "text-slate-900" : "text-blue-900"}`}>
                {selectedPeriodData.locked ? "🔒 Period Locked" : "Lock Period (Optional)"}
              </p>
              <p className={`text-sm mt-1 ${selectedPeriodData.locked ? "text-slate-600" : "text-blue-800"}`}>
                {selectedPeriodData.locked ? (
                  <>
                    This period is locked to prevent backdated edits or deletions to transactions within this date range.
                    Click "Unlock Period" to allow changes again.
                  </>
                ) : (
                  <>
                    Locking this period will prevent any edits or deletions to transactions within this date range.
                    You can apply values without locking the period.
                  </>
                )}
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => navigate("/month-end-stock")}
              >
                Back to List
              </Button>

              {/* Apply Values Button */}
              {selectedPeriodData.valuesApplied ? (
                <Button
                  variant="default"
                  className="bg-green-600/50 hover:bg-green-600/50 cursor-not-allowed opacity-60"
                  disabled={true}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Values Applied
                </Button>
              ) : (
                <Button
                  variant="default"
                  className="bg-orange-600 hover:bg-orange-700"
                  onClick={() => setShowApplyValuesDialog(true)}
                  disabled={isApplyingValues}
                >
                  {isApplyingValues && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                  <Download className="h-4 w-4 mr-2" />
                  Apply Values
                </Button>
              )}

              {/* Lock/Unlock Button */}
              {selectedPeriodData.locked ? (
                <Button
                  onClick={handleUnlockPeriod}
                  disabled={isUnlocking}
                  variant="outline"
                >
                  {isUnlocking && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                  <Unlock className="h-4 w-4 mr-2" />
                  Unlock Period
                </Button>
              ) : (
                <Button
                  onClick={handleLockPeriod}
                  disabled={isLocking}
                >
                  {isLocking && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                  <Lock className="h-4 w-4 mr-2" />
                  Lock Period
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Apply Values Confirmation Dialog */}
      <AlertDialog open={showApplyValuesDialog} onOpenChange={setShowApplyValuesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-orange-900">
              Apply Recalculated Values?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will update <span className="font-semibold">{selectedPeriodData.totalItems} items</span> with
                their recalculated quantities and weighted averages from the Month End Stock calculation.
              </p>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 dark:bg-orange-950/20 dark:border-orange-900/50 mt-3">
                <p className="font-semibold text-orange-900 text-sm">⚠️ Warning</p>
                <p className="text-sm text-orange-800 mt-1">
                  This action is <span className="font-semibold">permanent and cannot be undone</span>.
                  The current item quantities and weighted averages will be replaced with the recalculated values.
                </p>
              </div>
              <p className="text-sm mt-3">
                Are you sure you want to proceed?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isApplyingValues}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleApplyValues}
              disabled={isApplyingValues}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isApplyingValues && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Yes, Apply Values
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MonthEndStockDetail;
